/**
 * Titles (SPEC §5.1, D4): unique per graph, case-insensitive, so `[[Title]]` in text resolves to
 * exactly one node. One normalisation for every comparison: NFC, whitespace collapsed, trimmed,
 * lower-cased. Brackets are not allowed in titles, so every title can be written as `[[Title]]`.
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { MAX_TITLE, type Node } from './model.ts'

/** The canonical display form of a title as typed: NFC, whitespace collapsed, trimmed. */
export function normalizeTitle(raw: string): string {
  return raw.normalize('NFC').replace(/\s+/gu, ' ').trim()
}

/** The comparison key: two titles with the same key are the same title. */
export function titleKey(title: string): string {
  return normalizeTitle(title).toLowerCase()
}

/** Why a (normalised) title cannot be used, or null if it can. */
export function titleProblem(title: string): string | null {
  if (title === '') return 'A title cannot be empty.'
  if (title.length > MAX_TITLE) return `A title can have at most ${MAX_TITLE} characters.`
  if (/[[\]]/.test(title)) return 'Titles cannot contain [ or ].'
  if (/\p{Cc}/u.test(title)) return 'Titles cannot contain control characters.'
  return null
}

/** True for nodes whose title takes part in uniqueness and resolution. */
export function hasTitle(n: Node): boolean {
  return n.kind !== 'block' && n.title !== null && n.deletedAt === null
}

/** A live node other than `except` that already has this title, by scanning (exact, O(n)). */
export function titleOwner(g: Graph, title: string, except?: NodeId): Node | undefined {
  const key = titleKey(title)
  for (const n of g.nodes()) {
    if (n.id !== except && hasTitle(n) && titleKey(n.title!) === key) return n
  }
  return undefined
}

/**
 * Title → node for live titled nodes, kept current from graph change sets. Used for rendering
 * links and for the pickers; conflict rules use the exact scan (`titleOwner`) instead, because
 * they run inside batches where listeners have not fired yet.
 */
export class TitleIndex {
  private readonly g: Graph
  private readonly byKey = new Map<string, NodeId>()
  private readonly keyOf = new Map<NodeId, string>()
  private readonly listeners = new Set<() => void>()
  /** Incremented whenever a title appears, disappears or changes. */
  version = 0

  constructor(g: Graph) {
    this.g = g
    for (const n of g.nodes()) if (hasTitle(n)) this.update(n.id)
    g.subscribe((cs) => {
      let changed = false
      for (const id of cs.nodes) if (this.update(id)) changed = true
      if (changed) {
        this.version++
        this.listeners.forEach((f) => f())
      }
    })
  }

  private update(id: NodeId): boolean {
    const n = this.g.get(id)
    const next = n && hasTitle(n) ? titleKey(n.title!) : null
    if (next === null && !this.keyOf.has(id)) return false // untitled and unknown: the common case
    const prev = this.keyOf.get(id) ?? null
    if (prev === next) return false
    if (prev !== null) {
      this.keyOf.delete(id)
      if (this.byKey.get(prev) === id) {
        this.byKey.delete(prev)
        // A transient duplicate (two devices, one sync) takes over the key.
        for (const [other, k] of this.keyOf) {
          if (k === prev) {
            this.byKey.set(prev, other)
            break
          }
        }
      }
    }
    if (next !== null) {
      this.keyOf.set(id, next)
      if (!this.byKey.has(next)) this.byKey.set(next, id)
    }
    return true
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getVersion = (): number => this.version

  /** The live node with this title (any spelling of it), or null. */
  resolve(title: string): NodeId | null {
    const id = this.byKey.get(titleKey(title))
    return id !== undefined && this.g.isLive(id) ? id : null
  }

  /** Every live titled node. */
  *nodes(): IterableIterator<Node> {
    for (const id of this.keyOf.keys()) {
      const n = this.g.get(id)
      if (n && hasTitle(n)) yield n
    }
  }
}
